import { useState } from 'react';
import { createFileRoute, Link, Outlet, useMatchRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search } from 'lucide-react';

import { api } from '@/lib/api';
import { CLIENT_TYPE_LABELS } from '@/lib/clients';
import { requirePermission } from '@/lib/route-guards';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { QueryError } from '@/components/QueryError';

import type { Client } from '@logistech/ui-shared';

export const Route = createFileRoute('/_authed/clients')({
  beforeLoad: () => requirePermission('missions.read'),
  component: ClientsPage,
});

function ClientsPage() {
  const matchRoute = useMatchRoute();
  const isSubRoute = !!(matchRoute({ to: '/clients/new' }) || matchRoute({ to: '/clients/$id' }));
  if (isSubRoute) return <Outlet />;
  return <ClientsList />;
}

function ClientsList() {
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // L'API renvoie un tableau simple (pas de pagination) ; la recherche est faite côté client
  const query = useQuery({
    queryKey: ['clients', { showInactive }],
    queryFn: async () => (await api.get<Client[]>(`/clients?active=${showInactive ? 'false' : 'true'}`)).data,
  });
  const { data, isLoading } = query;

  const q = search.trim().toLowerCase();
  const clients = (data ?? []).filter((c) =>
    !q || [c.name, c.contactName, c.phone, c.email].some((f) => f?.toLowerCase().includes(q)),
  );

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Clients</h1>
        <Button asChild>
          <Link to="/clients/new">
            <Plus className="h-4 w-4 mr-2" /> Ajouter un client
          </Link>
        </Button>
      </div>
      <QueryError query={query} />

      <div className="flex items-center gap-4">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher par nom, contact, téléphone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Afficher les clients désactivés
        </label>
      </div>

      <div className="bg-card rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead>
              <TableHead>Secteur</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Téléphone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 7 }).map((__, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))
            )}
            {clients.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-semibold">{c.name}</TableCell>
                <TableCell>{CLIENT_TYPE_LABELS[c.type] ?? c.type}</TableCell>
                <TableCell>{c.contactName ?? '—'}</TableCell>
                <TableCell>{c.phone ?? '—'}</TableCell>
                <TableCell>{c.email ?? '—'}</TableCell>
                <TableCell>
                  <Badge variant={c.active ? 'default' : 'secondary'}>{c.active ? 'ACTIF' : 'DÉSACTIVÉ'}</Badge>
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/clients/$id" params={{ id: c.id }}>Détail</Link>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {data && clients.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  Aucun client trouvé
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
